// client/src/lib/sentry.ts
import * as Sentry from '@sentry/react';
import React from 'react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';

const isProduction = import.meta.env.PROD;
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const appRelease = import.meta.env.VITE_APP_VERSION || 'unknown';

/**
 * Initialize Sentry error tracking
 * Only initializes if DSN is provided and in production
 */
export function initSentry() {
  // Only initialize in production to avoid polluting Sentry with local development errors
  if (!sentryDsn || !isProduction) {
    if (!isProduction) {
      console.log('Sentry disabled for local development');
    }
    return;
  }

  try {
    Sentry.init({
      dsn: sentryDsn,
      release: `cfa-eval-client@${appRelease}`,

      // Environment
      environment: isProduction ? 'production' : 'development',

      // Performance monitoring
      tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in prod, 100% in dev

      // Outgoing requests that get trace headers attached (top-level init
      // option since Sentry v8; no longer part of BrowserTracingOptions)
      tracePropagationTargets: [
        'localhost',
        /^https:\/\/.*\.ld-growth\.com/,
        /^https:\/\/cfa-eval.*\.herokuapp\.com/
      ],


      // Session replay for debugging
      replaysSessionSampleRate: 0.1, // 10% of sessions
      replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
      
      // Integrations
      // NOTE: Session Replay is intentionally NOT started here. Its first act is
      // a synchronous snapshot of the whole DOM plus every stylesheet (~384 KB of
      // Tailwind), and with replaysOnErrorSampleRate > 0 it buffers in every
      // session. On a first visit on a budget phone that landed right as the
      // login form appeared and froze the main thread for most of a second.
      // It is added later by startSentryReplay() once the user is signed in and
      // the browser is idle (see AuthContext).
      integrations: [
        // Browser tracing with React Router route-change instrumentation.
        // (Registers the "BrowserTracing" integration; a separate plain
        // browserTracingIntegration() would be replaced by this one anyway.)
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect: React.useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      
      // Filter out noise
      ignoreErrors: [
        // Browser extensions
        'top.GLOBALS',
        'chrome-extension://',
        'moz-extension://',

        // In-app browser injections (Instagram, Facebook, TikTok, Meta Quest)
        'EmptyRanges',
        "Can't find variable: EmptyRanges",
        'iabjs://',

        // Samsung Internet / Android WebView Java bridge noise
        // Fires when JS calls into a Java bridge after the bridge has been GC'd.
        // Origin: Samsung browser internals, not our code.
        'Java object is gone',
        'enableButtonsClickedMetaDataLogging',

        // Network errors that are expected
        'NetworkError',
        'Failed to fetch',
        'Load failed',

        // ResizeObserver errors (harmless)
        'ResizeObserver loop limit exceeded',
        'ResizeObserver loop completed with undelivered notifications',

        // Chunk loading errors (handled by chunkRecovery auto-reload)
        /Loading chunk \d+ failed/,
        'ChunkLoadError',
        // iOS Safari stale-chunk errors after a deploy (handled by chunkRecovery)
        'Importing a module script failed',
        'is not a valid JavaScript MIME type',
        // Vite CSS preload 404 after a stale deploy (NODE-CD, handled by chunkRecovery)
        'Unable to preload CSS',

        // Microsoft Office / Outlook browser-extension noise (NODE-BN)
        // "Object Not Found Matching Id:1, MethodName:update, ParamCount:4"
        'Object Not Found Matching Id',
      ],
      
      // Add context to errors
      beforeSend(event, hint) {
        // Add user agent info
        if (event.request) {
          event.request.headers = {
            ...event.request.headers,
            'User-Agent': navigator.userAgent,
          };
        }

        // Add device info
        event.contexts = {
          ...event.contexts,
          device: {
            type: /Mobile|Android|iPhone|iPad/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
            online: navigator.onLine,
            memory: (performance as any).memory?.jsHeapSizeLimit,
          },
        };

        event.tags = {
          ...event.tags,
          app_release: appRelease,
          page_path: window.location.pathname,
        };

        // Add any lifecycle diagnostics from globals (for long-lived tab analysis)
        try {
          const globalAny = window as any;
          const now = Date.now();
          const appStartTime = typeof globalAny.__appStartTime === 'number' ? globalAny.__appStartTime : undefined;
          const lastVisibilityChangeTimestamp = typeof globalAny.__lastVisibilityChangeTimestamp === 'number'
            ? globalAny.__lastVisibilityChangeTimestamp
            : undefined;
          const visibilityState = typeof globalAny.__visibilityState === 'string' ? globalAny.__visibilityState : undefined;

          event.extra = {
            ...event.extra,
            app_lifecycle: {
              appStartTime,
              lastVisibilityChangeTimestamp,
              visibilityState,
              timeSinceAppStartMs: appStartTime ? now - appStartTime : undefined,
              timeSinceLastVisibilityChangeMs: lastVisibilityChangeTimestamp ? now - lastVisibilityChangeTimestamp : undefined,
            },
          };
        } catch {
          // Best-effort only
        }

        // Check if this is a chunk loading error
        const error = hint.originalException;
        if (error && typeof error === 'object' && 'message' in error) {
          const message = String(error.message).toLowerCase();
          if (
            message.includes('chunk') ||
            message.includes('dynamically imported module') ||
            message.includes('failed to fetch')
          ) {
            // Add special tag for chunk errors
            event.tags = {
              ...event.tags,
              error_type: 'chunk_loading',
            };
          }
        }

        // Filter out expected 401 errors on login page
        // These are normal during initial auth phase when user hasn't logged in yet
        const url = event.request?.url || window.location.href;

        // Suppress 401 errors on login page - these are expected
        if (url.includes('/login')) {
          if (event.exception) {
            const exception = event.exception.values?.[0];
            const message = exception?.value || '';
            const type = exception?.type || '';

            if (message.includes('401') || type.includes('401')) {
              return null;
            }
          }

          // Also check breadcrumbs for 401 errors
          if (event.breadcrumbs) {
            const has401 = event.breadcrumbs.some(b =>
              b.message?.includes('401') ||
              b.data?.status === 401 ||
              b.data?.statusCode === 401
            );
            if (has401) {
              return null;
            }
          }
        }

        return event;
      },
      
      // Breadcrumbs for debugging
      beforeBreadcrumb(breadcrumb) {
        // Filter out noisy console logs
        if (breadcrumb.category === 'console' && breadcrumb.level === 'log') {
          return null;
        }
        return breadcrumb;
      },
    });
  } catch (error) {
  }
}

/**
 * Capture an exception with additional context
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (!sentryDsn) return;
  
  Sentry.withScope((scope) => {
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureException(error);
  });
}

/**
 * Capture a message with additional context
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) {
  if (!sentryDsn) return;
  
  Sentry.withScope((scope) => {
    scope.setLevel(level);
    if (context) {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
    }
    Sentry.captureMessage(message);
  });
}

export interface SentryUserContext {
  id: string;
  email?: string;
  username?: string;
  storeId?: string;
  storeNumber?: string;
  storeName?: string;
  role?: string;
  position?: string;
  accessLevel?: string;
  isAdmin?: boolean;
}

/**
 * Build Sentry user context from auth session data
 */
export function buildSentryUserFromSession(
  user: {
    _id: string;
    email?: string;
    name?: string;
    role?: string;
    position?: string;
    accessLevel?: string;
    isAdmin?: boolean;
    store?: { _id?: string; storeNumber?: string; name?: string };
  } | null,
  store?: { _id?: string; storeNumber?: string; name?: string } | null
): SentryUserContext | null {
  if (!user?._id) return null;

  const activeStore = store || user.store;

  return {
    id: String(user._id),
    email: user.email,
    username: user.name,
    storeId: activeStore?._id ? String(activeStore._id) : undefined,
    storeNumber: activeStore?.storeNumber,
    storeName: activeStore?.name,
    role: user.role,
    position: user.position,
    accessLevel: user.accessLevel,
    isAdmin: user.isAdmin,
  };
}

/**
 * Set user context for error tracking
 */
export function setUser(user: SentryUserContext | null) {
  if (!sentryDsn) return;

  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      username: user.username,
    });

    if (user.storeId) {
      Sentry.setTag('store_id', user.storeId);
    }
    if (user.storeNumber) {
      Sentry.setTag('store_number', user.storeNumber);
    }
    if (user.role) {
      Sentry.setTag('user_role', user.role);
    }
    if (user.accessLevel) {
      Sentry.setTag('access_level', user.accessLevel);
    }
    if (user.position) {
      Sentry.setTag('user_position', user.position);
    }
    if (user.isAdmin !== undefined) {
      Sentry.setTag('is_admin', String(user.isAdmin));
    }

    Sentry.setContext('store', {
      id: user.storeId,
      number: user.storeNumber,
      name: user.storeName,
    });

    Sentry.setContext('user_profile', {
      role: user.role,
      position: user.position,
      accessLevel: user.accessLevel,
      isAdmin: user.isAdmin,
    });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Add breadcrumb for debugging
 */
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  if (!sentryDsn) return;
  
  Sentry.addBreadcrumb({
    message,
    category,
    level: 'info',
    data,
  });
}

// Re-export Sentry for direct use if needed
export { Sentry };

let replayStarted = false;

/**
 * Start Sentry Session Replay after the user is signed in and the browser is
 * idle. Deferred out of initSentry() so the replay DOM/stylesheet snapshot never
 * competes with first paint of the login page. Safe to call repeatedly; it only
 * adds the integration once per page load.
 */
export function startSentryReplay() {
  if (replayStarted || !sentryDsn || !isProduction) return;
  if (!Sentry.getClient()) return;
  replayStarted = true;

  const start = () => {
    try {
      Sentry.addIntegration(
        Sentry.replayIntegration({
          maskAllText: true, // Privacy: mask all text
          blockAllMedia: true, // Privacy: block all media
        })
      );
    } catch {
      // Best-effort only; never let diagnostics break the app
    }
  };

  const w = window as any;
  if (typeof w.requestIdleCallback === 'function') {
    w.requestIdleCallback(start, { timeout: 10000 });
  } else {
    setTimeout(start, 3000);
  }
}
